import { Lightning, Registry } from '@lightningjs/sdk';

export function debounce<TArgs extends unknown[]>(
  fn: (...args: TArgs) => unknown,
  timeout = 1000,
) {
  const debouncer = debounceWithCancel(fn, timeout);
  return debouncer.debounce;
}

export function debounceWithCancel<TArgs extends unknown[]>(
  fn: (...args: TArgs) => unknown,
  timeout = 1000,
) {
  let timer: ReturnType<typeof Registry.setTimeout>;

  const debouncer = (...args: TArgs) => {
    if (timer) Registry.clearTimeout(timer);

    timer = Registry.setTimeout(() => {
      fn(...args);
      timer = undefined;
    }, timeout);
  };

  const cancel = () => {
    if (timer) {
      Registry.clearTimeout(timer);
      timer = undefined;
    }
  };

  return {
    debounce: debouncer,
    cancel: cancel,
  };
}

// TODO: confirm if this is the best way to check/get window dimensions
export const is1280x720p = () => {
  return window.innerWidth === 1280 || window.innerHeight === 720;
};

export const getScalingMultiplier = () => {
  return is1280x720p() ? 1.5 : 1;
};

export const getImageTextureObj = (
  imagePath: string,
  width: number,
  height: number,
): Lightning.Element.PatchTemplate<any> => {
  // using getSvgTexture for data: is a bit of a hack that uses the DOM instead of web workers for rendering images
  // on old devices errors seem to occur due to web workers using XmlHttpRequest for data: (probably due to actual fetch requests being made)
  if (imagePath.endsWith('.svg') || imagePath.startsWith('data:')) {
    return {
      texture: Lightning.Tools.getSvgTexture(imagePath, width, height),
    };
  } else {
    return {
      texture: {
        type: Lightning.textures.ImageTexture,
        src: imagePath,
        resizeMode: { type: 'cover', w: width, h: height, clipY: 0 },
      },
    };
  }
};

export const getImageTextureContainObj = (
  imagePath: string,
  width: number,
  height: number,
): Lightning.Element.PatchTemplate<any> => {
  return {
    texture: {
      type: Lightning.textures.ImageTexture,
      src: imagePath,
      resizeMode: {
        type: 'contain',
        w: width,
        h: height,
      },
    },
  };
};

export function getLastChildLeaf(ref: Lightning.Element) {
  if (!ref.hasChildren()) return ref;

  let currentChild = ref.children[ref.children.length - 1] as Lightning.Element;
  while (currentChild?.hasChildren()) {
    currentChild = currentChild.children[
      currentChild.children.length - 1
    ] as Lightning.Element;
  }
  return currentChild;
}

/* 
Call to manually load textures in advance to improve loading delays
Textures loaded by this will get cleared by GC upon detaching
*/
export function preloadTextures(
  context: Lightning.Component,
  textures: Lightning.Element.PatchTemplate<any>[],
) {
  textures.forEach(template => {
    context.stage.element(template).loadTexture();
  });
}

/**
 * Returns the focus depth of the given component
 *
 * @param application - Lightning application
 * @param component - Lightning component
 * @returns the focus depth of the given component if component exists in the focus path, otherwise, returns 0
 */
export const getFocusDepth = (
  application: Lightning.Application,
  component: Lightning.Component,
) => {
  const { focusPath = [] } = application;
  for (let i = 0; i < focusPath.length; i++) {
    if (focusPath[i]?.id === component.id) return i;
  }

  return 0;
};

export const ttsTagRemoval = (text: string) => {
  return text.replace(/<[^<>]*>/g, '');
};

export const debounceAnnounce = (
  component: Lightning.Component,
  phrase: string,
  delay = 500,
) => {
  component.fireAncestors('$announcerCancel');
  debounce((phrase: string) => {
    component.fireAncestors('$announce', phrase, { append: true });
  }, delay)(phrase);
};

export const roundMilliseconds = (time: number) => {
  return Math.ceil(time * 1000) / 1000;
};

/**
 * Gets the base domain of a URL. Path should be in the form [http|https]://[DOMAIN]/[PARAMS]
 * @param url URL string we want to get the base domain from
 */
export const getBaseDomainFromUrl = (url: string) => {
  const splitUrl = url.split('/');
  if (splitUrl.length < 3) return '';

  const protocol = splitUrl[0];
  if (protocol !== 'http:' && protocol !== 'https:') return '';

  const domain = splitUrl[2];
  return `${protocol}//${domain}`;
};

export const convertHexColor = (
  color: string,
  hexOpacity = 'ff',
): number | undefined => {
  let colorValue: string;

  if (color.startsWith('#')) {
    colorValue = `0x${hexOpacity}${color.split('#')[1] ?? '0'}`;
  } else if (color.startsWith('0x')) {
    colorValue = color;
  } else {
    colorValue = `0x${hexOpacity}${color}`;
  }

  const value = Number(colorValue);
  if (Number.isNaN(value)) return undefined;

  return value;
};

export const percentToHex = (percentage: number) => {
  const val = Math.round((percentage / 100) * 255);
  const hex = val.toString(16).padStart(2, '0');
  return hex.toUpperCase();
};

export const separateHexColorAlpha = (hex: string) => {
  if (hex.startsWith('#') && hex.length === 9) {
    const color = hex.slice(0, 7);
    const alpha = hex.slice(7);
    return { color, alpha };
  }
};

export const isVersionNewer = (newVersion: string, oldVersion: string) => {
  const versionSplit = newVersion.split('.');
  const minimumVersionSplit = oldVersion.split('.');
  const len = Math.min(newVersion.length, oldVersion.length);

  for (let i = 0; i < len; i++) {
    const versionNum = parseInt(versionSplit[i]!);
    const minimumVersionNum = parseInt(minimumVersionSplit[i]!);

    if (versionNum < minimumVersionNum) {
      return false;
    } else if (versionNum > minimumVersionNum) {
      return true;
    }
  }

  return newVersion.length >= oldVersion.length;
};

export const generateUUIDv4 = (): string => {
  // randomUUID is supported since chrome version 92, we need to handle support for older browsers
  if (!!window.crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Create an array of 16 random bytes
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);

  // Set version (4) and variant bits according to RFC 4122
  bytes[6] = (bytes[6]! & 0x0f) | 0x40; // version 4 (clears the first four bits and sets them to 0b0100)
  bytes[8] = (bytes[8]! & 0x3f) | 0x80; // variant (clears the first two bits and sets them to 0b10)

  // Convert to UUID string
  const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0'));
  return (
    hex.slice(0, 4).join('') +
    '-' +
    hex.slice(4, 6).join('') +
    '-' +
    hex.slice(6, 8).join('') +
    '-' +
    hex.slice(8, 10).join('') +
    '-' +
    hex.slice(10, 16).join('')
  );
};

export const appendUrlParams = (
  url: string,
  params: Record<string, string | number>,
) => {
  const entries = Object.entries(params);
  if (!entries.length) return url;

  const separator = url.includes('?') ? '&' : '?';
  const query = entries
    .map(([key, value]) => {
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .join('&');

  return `${url}${separator}${query}`;
};

export const decodeBase64 = (value: string) => {
  return atob(value);
};
